// Subscription validators

import { z } from 'zod';
import { dateSchema, currencyCodeSchema } from '../utils/validation';

export const subscriptionIntervalEnumSchema = z.enum(['monthly', 'quarterly', 'yearly']);

export const subscriptionKindEnumSchema = z.enum([
  'domain',
  'hosting',
  'service',
  'license',
  'other',
]);

/**
 * What a person fills in.
 *
 * Five required fields: who it is for, what it is called, what it costs, how
 * often, and when it next falls due. Everything else has a default, because
 * nobody renewing a domain should have to think about line items or grace days.
 */
export const createSubscriptionSchema = z.object({
  customerId: z.string().min(1),
  name: z.string().min(1).max(200),
  /** Per-period price. Becomes a single line item internally. */
  amount: z.number().positive(),
  interval: subscriptionIntervalEnumSchema,
  /** End of the current period — the date the renewal falls due. */
  nextDueDate: dateSchema,

  kind: subscriptionKindEnumSchema.default('service'),
  identifier: z.string().max(255).optional(),
  currency: currencyCodeSchema.default('IDR'),
  taxRate: z.number().min(0).max(100).default(0),
  /** Registrar grace window. Decides when a renewal is recorded as lost —
   *  it does not schedule messages. */
  graceDays: z.number().int().min(0).max(90).optional(),
  /** Days before the due date at which notices go out. Defaults to 30/14/7/1. */
  reminderOffsetDays: z.array(z.number().int()).optional(),
  autoInvoice: z.boolean().default(true),
  startDate: dateSchema.optional(),
  notes: z.string().max(2000).optional(),
});

export const updateSubscriptionSchema = createSubscriptionSchema
  .partial()
  .omit({ customerId: true });

export type CreateSubscriptionInput = z.infer<typeof createSubscriptionSchema>;
export type UpdateSubscriptionInput = z.infer<typeof updateSubscriptionSchema>;
